import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import WorkRoundedIcon from "@mui/icons-material/WorkRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { Link as RouterLink } from "react-router";
import { useSelector } from "react-redux";
import { useMemo } from "react";

import AHDate from "_shared/dates/date";
import {
  DashboardCardEyebrow,
  DashboardCardHeader,
  DashboardCardTitle,
  DashboardCardState,
  DashboardPanelCard,
  DashboardRowLink,
  DashboardRowList,
  DashboardRowMarker,
  DashboardRowText,
} from "_shared/_views/dashboards/dashboard-card";
import { KeptTrail, KeptTrailLead, KeptTrailLine } from "_shared/_views/kept-trail/kept-trail-line";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import type { IndividualJSON } from "marketplace/individuals/_schema/individual-schema";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { useSession } from "users/__session/session-context";
import {
  useGetActiveWorkAgreementsCountQuery,
  useGetActiveWorkAgreementsQuery,
} from "work/work-agreements/_api/work-agreement-api";
import type { WorkAgreementJSON } from "work/work-agreements/_schemas/work-agreement-schema";
import WorkVersion from "work/work-agreements/versions/work-version";

const RowInitial = styled(Typography)({
  fontWeight: 700,
  lineHeight: 1,
});

/**
 * The work agreement list's current work, said outright (#6716): a card's link names what it shows, so
 * the reader's remembered view of the list does not replace it (#6714).
 */
export const activeWorkRoute = "/work?stage=open";
export const dashboardActiveWorkLimit = 3;

export function workAgreementRoute(workAgreementId: string): string {
  return `/work/active/view/${workAgreementId}`;
}

export type ActiveWorkRow = {
  id: string;
  name: string;
  /** Which version of the work it is, "v2" (#6636). Absent from a payload older than the numbers. */
  version?: string;
  startDate?: string;
  counterpartyUserId?: string;
};

/**
 * The rows, read off the list response rather than off a deserialized `WorkAgreement`.
 *
 * Every field a row needs — id, name, version numbers, start date, and the counterparty's user id —
 * is a plain value on the response, and the work-agreement model is large enough that a record
 * failing to deserialize for a reason this card does not care about would cost the card a row. The
 * page's own table carries a whole error path for exactly that.
 *
 * The counterparty is the team on the other side of the agreement from the viewer, which is why the
 * context matters twice: it decides which agreements come back at all, and which team is "them".
 * A team the viewer may not read arrives masked and with no leader, and then the row simply has no
 * counterparty line rather than an empty one.
 */
export function activeWorkRows(
  agreements: WorkAgreementJSON[],
  viewingAsVendor: boolean,
): ActiveWorkRow[] {
  return agreements
    .filter((agreement): agreement is WorkAgreementJSON & { id: string } => Boolean(agreement.id))
    .map((agreement) => {
      const counterpartyTeam = viewingAsVendor ? agreement.clientTeam : agreement.vendorTeam;
      return {
        id: agreement.id,
        name: agreement.name?.trim() || "Untitled agreement",
        version: WorkVersion.fromNumbers(agreement)?.label,
        startDate: agreement.startDate ?? undefined,
        counterpartyUserId: counterpartyTeam?.isMasked ? undefined : counterpartyTeam?.leader?.userId,
      };
    });
}

/** The row's name as a reader tells it from the work's other versions: "10/13 Test · v2". */
function nameWithVersion(row: ActiveWorkRow): string {
  return row.version ? `${row.name}${WorkVersion.SEPARATOR}${row.version}` : row.name;
}

export function startedLabel(startDate: string | undefined): string {
  if (!startDate) return "No start date";
  const started = AHDate.fromJSON(startDate);
  if (!started?.isValid) return "No start date";
  return `Started ${started.format("MMM D, YYYY")}`;
}

/**
 * The work this user already has, as a list of doors rather than a number.
 *
 * "12 active agreements" changes nothing the reader does next; three agreements they can open do.
 * The total still has a job — it belongs in the header beside the link to the full list — and it
 * comes from the same context the rows do, so the two cannot disagree (#5078).
 */
export default function ActiveWorkCard() {
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  // Work is permission-gated at the route, the way the queue card gates its network row.
  const includesWork = sessionPermissions?.includesAccessToWork === true;

  const countQuery = useGetActiveWorkAgreementsCountQuery({ session }, { skip: !includesWork });
  const agreementsQuery = useGetActiveWorkAgreementsQuery(
    { session, pageSize: dashboardActiveWorkLimit },
    { skip: !includesWork },
  );

  const rows = useMemo(
    () => activeWorkRows(agreementsQuery.data?.data ?? [], session.context?.viewingAsVendor === true),
    [agreementsQuery.data, session.context?.viewingAsVendor],
  );
  const counterpartyUserIds = useMemo(
    () => new Set(rows.map((row) => row.counterpartyUserId).filter((id): id is string => Boolean(id))),
    [rows],
  );
  const counterpartiesQuery = useGetIndividualsByUserIdsQuery(
    { userIds: [...counterpartyUserIds] },
    { skip: counterpartyUserIds.size === 0 },
  );
  const counterpartyNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const individual of (counterpartiesQuery.data ?? []) as IndividualJSON[]) {
      const fullName = [individual.firstName, individual.lastName].filter(Boolean).join(" ").trim();
      if (individual.userId && fullName) names.set(individual.userId, fullName);
    }
    return names;
  }, [counterpartiesQuery.data]);

  const total = countQuery.data ?? 0;
  const additionalAgreementCount = Math.max(0, total - rows.length);
  const isLoading = countQuery.isLoading || agreementsQuery.isLoading;
  // A counterparty name that could not be loaded costs a row its second line, not the card.
  const isError = countQuery.isError || agreementsQuery.isError;

  if (!includesWork) return null;

  return (
    <DashboardPanelCard>
      <DashboardCardHeader>
        <Box>
          <DashboardCardEyebrow>
            <WorkRoundedIcon color="primary" fontSize="small" />
            <Typography color="primary" variant="overline">Your work</Typography>
          </DashboardCardEyebrow>
          <DashboardCardTitle label="Your work" to={activeWorkRoute}>
            {total > 0 ? `${total} active ${total === 1 ? "agreement" : "agreements"}` : "Active work"}
          </DashboardCardTitle>
        </Box>
      </DashboardCardHeader>
      {isLoading && (
        <DashboardCardState role="status">
          <CircularProgress size={20} />
          <Typography variant="body2">Loading your active work…</Typography>
        </DashboardCardState>
      )}
      {!isLoading && isError && (
        <DashboardCardState>
          <Typography color="error" variant="body2">Your active work could not be loaded.</Typography>
        </DashboardCardState>
      )}
      {!isLoading && !isError && rows.length === 0 && (
        <DashboardCardState>
          <Typography variant="body2">No active work yet.</Typography>
        </DashboardCardState>
      )}
      {!isLoading && !isError && rows.length > 0 && (
        <DashboardRowList>
          <Stack spacing={1}>
            {rows.map((row) => {
              const counterparty = row.counterpartyUserId
                ? counterpartyNames.get(row.counterpartyUserId)
                : undefined;
              return (
                <DashboardRowLink
                  key={row.id}
                  to={workAgreementRoute(row.id)}
                  aria-label={`Open ${nameWithVersion(row)}`}
                >
                  <DashboardRowMarker aria-hidden="true" emphasis="quiet">
                    <RowInitial variant="h6">{row.name.slice(0, 1).toUpperCase()}</RowInitial>
                  </DashboardRowMarker>
                  <DashboardRowText>
                    <KeptTrailLine variant="subtitle2">
                      <KeptTrailLead>{row.name}</KeptTrailLead>
                      {row.version && (
                        <KeptTrail>{`${WorkVersion.SEPARATOR}${row.version}`}</KeptTrail>
                      )}
                    </KeptTrailLine>
                    <Typography color="text.secondary" noWrap variant="caption">
                      {counterparty ? `${counterparty} · ${startedLabel(row.startDate)}` : startedLabel(row.startDate)}
                    </Typography>
                  </DashboardRowText>
                  <ArrowForwardRoundedIcon color="action" fontSize="small" />
                </DashboardRowLink>
              );
            })}
          </Stack>
          {additionalAgreementCount > 0 && (
            <Button component={RouterLink} to={activeWorkRoute} size="small">
              +{additionalAgreementCount} more {additionalAgreementCount === 1 ? "agreement" : "agreements"}
            </Button>
          )}
        </DashboardRowList>
      )}
    </DashboardPanelCard>
  );
}
